import { useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useGetBudgetSubCategories } from "@/services/queries/budgets";
import { Anchor, CaretLeft } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import Loading from "@/components/custom/loading";
import Card from "@/components/custom/card";
import { currencyFormatter } from "@/lib/utils";

const BudgetDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isPending, isError } = useGetBudgetSubCategories({
    id: id || "",
  });

  const budgetSubCategories = useMemo(() => data?.value || [], [data]);

  useEffect(() => {
    if (isError) {
      navigate("/budgets");
    }
  }, [isError, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="mt-8 px-5">
      <Link to={`/budgets`}>
        <CaretLeft className="text-white" size={16} />
      </Link>
      <div className="mt-4 flex flex-col gap-4">
        {isPending ? (
          <Loading className="mt-5" />
        ) : budgetSubCategories.length > 0 ? (
          budgetSubCategories.map(
            ({ id, name, allocatedAmount, currencyCode }) => (
              <BudgetDetailCard
                key={id}
                id={id}
                name={name}
                amount={allocatedAmount}
                currencyCode={currencyCode}
              />
            )
          )
        ) : (
          <div className="mt-5 gap-4 text-center font-medium leading-5 text-white">
            {t("title.not_found_sub_category_for_selected_category")}
          </div>
        )}
      </div>
    </div>
  );
};

const BudgetDetailCard = ({
  name,
  amount,
  id,
  currencyCode,
}: {
  name: string;
  amount: number;
  id: string;
  currencyCode: string;
}) => {
  return (
    <Card data-testid={`budget-details-card-${id}`} key={id}>
      <div className="flex flex-row items-center justify-center gap-[14px]">
        <span className="flex items-center justify-center text-white/50">
          <Anchor size={20} />
        </span>

        <span className="text-base font-medium leading-5 text-white">
          {name}
        </span>
      </div>

      <span
        data-testid={`category-amount-${id}`}
        className="text-[15px] font-medium leading-5 text-[#7ECEE8]"
      >
        {currencyFormatter(amount, currencyCode)}
      </span>
    </Card>
  );
};

export default BudgetDetails;
